import { PersistedWorkflow } from '@cotera/sdk/core';
import { asRecord, readString } from '../components/utils';
import type { WorkflowNodeViewData } from '../components/node/types';
import type {
  WorkflowCronTriggerViewData,
  WorkflowDatasetTriggerViewData,
  WorkflowEventTriggerViewData,
  WorkflowTriggerViewData,
  WorkflowWebhookTriggerViewData,
} from '../components/trigger/types';
import { agentIdFromNode } from '../components/node/agent-node-display';
import { agentNodeStrategy } from '../components/node/strategies/agent.node-strategy';
import { forEachNodeStrategy } from '../components/node/strategies/for-each.node-strategy';
import { adHocAgentNodeStrategy } from '../components/node/strategies/ad-hoc-agent.node-strategy';
import { completeToolCallNodeStrategy } from '../components/node/strategies/complete-tool-call-node-strategy';
import { toolCallNodeStrategy } from '../components/node/strategies/tool-call.node-strategy';
import { filterNodeStrategy } from '../components/node/strategies/filter.node-strategy';
import { debounceNodeStrategy } from '../components/node/strategies/debounce.node-strategy';
import { emitEventNodeStrategy } from '../components/node/strategies/emit-event.node-strategy';
import { mapperNodeStrategy } from '../components/node/strategies/mapper.node-strategy';
import { bucketNodeStrategy } from '../components/node/strategies/bucket.node-strategy';
import { groupByNodeStrategy } from '../components/node/strategies/group-by.node-strategy';
import { runWorkflowNodeStrategy } from '../components/node/strategies/run-workflow.node-strategy';
import {
  WORKFLOW_RUN_INPUT_NODE_TYPE,
  runInputNodeStrategy,
} from '../components/node/strategies/run-input.node-strategy';
import { unknownNodeStrategy } from '../components/node/strategies/unknown.node-strategy';
import { cronTriggerStrategy } from '../components/trigger/strategies/cron-trigger-strategy';
import { webhookTriggerStrategy } from '../components/trigger/strategies/webhook-trigger-strategy';
import { eventTriggerStrategy } from '../components/trigger/strategies/event-trigger-strategy';
import { datasetTriggerStrategy } from '../components/trigger/strategies/dataset-trigger-strategy';
import { unknownTriggerStrategy } from '../components/trigger/strategies/unknown-trigger-strategy';
import type { WorkflowGraphApiData } from '../workflow-graph.types';
import { WorkflowAgentNodeModel } from '../workflow-node-models/workflow-agent-node.model';
import {
  WorkflowCronTriggerNodeModel,
  type CronTriggerNodeSnapshot,
} from '../workflow-node-models/workflow-cron-trigger-node.model';
import {
  WorkflowEventTriggerNodeModel,
  type EventTriggerNodeSnapshot,
} from '../workflow-node-models/workflow-event-trigger-node.model';
import {
  WorkflowDatasetTriggerNodeModel,
  type DatasetTriggerNodeSnapshot,
} from '../workflow-node-models/workflow-dataset-trigger-node.model';
import { WorkflowWebhookTriggerFullNodeModel } from '../workflow-node-models/workflow-webhook-trigger-full-node.model';
import { WorkflowWebhookTriggerPreviewNodeModel } from '../workflow-node-models/workflow-webhook-trigger-preview-node.model';
import type { WorkflowWebhookTriggerNodeModel } from '../workflow-node-models/webhook-trigger-node.types';
import {
  readWebhookHasSecretFromGraph,
  readWebhookInstallStatusFromGraph,
  readWebhookPreviewStateFromGraph,
} from '../workflow-node-models/webhook-trigger-graph';
import type {
  WorkflowGraphNodeRenderableNode,
  WorkflowNodeModelFactoryContext,
  WorkflowRenderCatalog,
  WorkflowTriggerRenderableNode,
  WorkflowDisposable,
} from './types';
import {
  renderAgentFlowNode,
  renderForEachFlowNode,
  renderToolCallFlowAffordances,
} from './graph-node-flow-renderers';
import { WORKFLOW_RENDER_NODE_KEYS } from './workflow-render-catalog-keys';

const { PersistedWorkflowTriggerKind, WorkflowNodeType } = PersistedWorkflow;

function isWebhookTrigger(
  trigger: WorkflowTriggerViewData
): trigger is WorkflowWebhookTriggerViewData {
  return trigger.kind === PersistedWorkflowTriggerKind.Webhook;
}

function isCronTrigger(
  trigger: WorkflowTriggerViewData
): trigger is WorkflowCronTriggerViewData {
  return trigger.kind === PersistedWorkflowTriggerKind.Cron;
}

function isEventTrigger(
  trigger: WorkflowTriggerViewData
): trigger is WorkflowEventTriggerViewData {
  return trigger.kind === PersistedWorkflowTriggerKind.Event;
}

function isDatasetTrigger(
  trigger: WorkflowTriggerViewData
): trigger is WorkflowDatasetTriggerViewData {
  return trigger.kind === PersistedWorkflowTriggerKind.Dataset;
}

function readCronSnapshotFromGraph(
  graph: WorkflowGraphApiData,
  triggerId: string
): CronTriggerNodeSnapshot | undefined {
  for (const snapshot of graph.triggers) {
    if (
      snapshot.kind === PersistedWorkflowTriggerKind.Cron &&
      snapshot.trigger.id === triggerId
    ) {
      return {
        id: snapshot.trigger.id,
        name: snapshot.trigger.name,
        cron: snapshot.trigger.cron,
        timezone: snapshot.trigger.timezone,
        enabled: snapshot.trigger.enabled,
      };
    }
  }
  return undefined;
}

function readEventSnapshotFromGraph(
  graph: WorkflowGraphApiData,
  triggerId: string
): EventTriggerNodeSnapshot | undefined {
  for (const snapshot of graph.triggers) {
    if (
      snapshot.kind === PersistedWorkflowTriggerKind.Event &&
      snapshot.trigger.id === triggerId
    ) {
      return {
        id: snapshot.trigger.id,
        name: snapshot.trigger.name,
        eventName: snapshot.trigger.eventName,
        enabled: snapshot.trigger.enabled,
      };
    }
  }
  return undefined;
}

function readAgentIdsFromGraph(graph: WorkflowGraphApiData): string[] {
  const ids: string[] = [];
  for (const node of graph.nodes) {
    if (node.type !== WorkflowNodeType.Agent) {
      continue;
    }
    const agentId = readString(asRecord(node.data), 'agentId');
    if (agentId !== undefined && agentId !== '') {
      ids.push(agentId);
    }
  }
  return ids;
}

function emptyGraph(): WorkflowGraphApiData {
  return { nodes: [], edges: [], triggerBindings: [], triggers: [] };
}

const webhookTriggerRenderableNode: WorkflowTriggerRenderableNode<
  WorkflowTriggerViewData,
  WorkflowWebhookTriggerNodeModel,
  WorkflowWebhookTriggerNodeModel
> = {
  key: WORKFLOW_RENDER_NODE_KEYS.webhookTrigger,
  layer: 'trigger',
  matches: isWebhookTrigger,
  view: webhookTriggerStrategy,
  models: {
    listEntityIdsInGraph: (graph) =>
      graph.triggers
        .filter(
          (snapshot) => snapshot.kind === PersistedWorkflowTriggerKind.Webhook
        )
        .map((snapshot) => snapshot.trigger.id),
    wantsPreview: (ctx, entityId) => ctx.isPreviewEntity(entityId),
    createPreviewModel: (ctx, triggerId) =>
      new WorkflowWebhookTriggerPreviewNodeModel(
        triggerId,
        readWebhookPreviewStateFromGraph(ctx.graph ?? emptyGraph(), triggerId)
      ),
    createFullModel: (ctx, triggerId) => {
      const graph = ctx.graph ?? emptyGraph();
      const model = new WorkflowWebhookTriggerFullNodeModel({
        triggerId,
        initialInstallStatus: readWebhookInstallStatusFromGraph(
          graph,
          triggerId
        ),
        initialHasSecret: readWebhookHasSecretFromGraph(graph, triggerId),
        client: ctx.client,
        watchablesFactory: ctx.watchablesFactory,
      });
      const preview = readWebhookPreviewStateFromGraph(graph, triggerId);
      if (preview !== undefined) {
        model.reconcileFromGraph({
          installStatus: preview.installStatus,
          hasSecret: preview.hasSecret,
        });
      }
      return model;
    },
    syncEntityFromGraph: (_ctx, _triggerId, model, graph) => {
      const preview = readWebhookPreviewStateFromGraph(graph, _triggerId);
      if (preview === undefined) {
        return;
      }
      if (model.mode === 'preview') {
        model.syncFromGraph(preview);
      } else {
        model.reconcileFromGraph({
          installStatus: preview.installStatus,
          hasSecret: preview.hasSecret,
        });
      }
    },
  },
};

const cronTriggerRenderableNode: WorkflowTriggerRenderableNode<
  WorkflowTriggerViewData,
  WorkflowCronTriggerNodeModel
> = {
  key: WORKFLOW_RENDER_NODE_KEYS.cronTrigger,
  layer: 'trigger',
  matches: isCronTrigger,
  view: cronTriggerStrategy,
  models: {
    listEntityIdsInGraph: (graph) =>
      graph.triggers
        .filter(
          (snapshot) => snapshot.kind === PersistedWorkflowTriggerKind.Cron
        )
        .map((snapshot) => snapshot.trigger.id),
    createFullModel: (ctx, triggerId) => {
      const snapshot =
        ctx.graph === null
          ? undefined
          : readCronSnapshotFromGraph(ctx.graph, triggerId);
      return new WorkflowCronTriggerNodeModel(triggerId, {
        id: triggerId,
        name: snapshot?.name ?? 'Schedule',
        cron: snapshot?.cron ?? '',
        timezone: snapshot?.timezone ?? 'UTC',
        enabled: snapshot?.enabled ?? true,
      });
    },
    syncEntityFromGraph: (_ctx, triggerId, model, graph) => {
      const snapshot = readCronSnapshotFromGraph(graph, triggerId);
      if (snapshot !== undefined) {
        model.updateSnapshot(snapshot);
      }
    },
  },
};

function readDatasetSnapshotFromGraph(
  graph: WorkflowGraphApiData,
  triggerId: string
): DatasetTriggerNodeSnapshot | undefined {
  for (const snapshot of graph.triggers) {
    if (
      snapshot.kind === PersistedWorkflowTriggerKind.Dataset &&
      snapshot.trigger.id === triggerId
    ) {
      return {
        id: snapshot.trigger.id,
        name: snapshot.trigger.name,
        datasetId: snapshot.trigger.datasetId,
        criterion: snapshot.trigger.criterion,
        trackFields: snapshot.trigger.trackFields,
        processExistingRows: snapshot.trigger.processExistingRows,
        runMode: snapshot.trigger.runMode,
        enabled: snapshot.trigger.enabled,
      };
    }
  }
  return undefined;
}

const datasetTriggerRenderableNode: WorkflowTriggerRenderableNode<
  WorkflowTriggerViewData,
  WorkflowDatasetTriggerNodeModel
> = {
  key: WORKFLOW_RENDER_NODE_KEYS.datasetTrigger,
  layer: 'trigger',
  matches: isDatasetTrigger,
  view: datasetTriggerStrategy,
  models: {
    listEntityIdsInGraph: (graph) =>
      graph.triggers
        .filter(
          (snapshot) => snapshot.kind === PersistedWorkflowTriggerKind.Dataset
        )
        .map((snapshot) => snapshot.trigger.id),
    createFullModel: (ctx, triggerId) => {
      const snapshot =
        ctx.graph === null
          ? undefined
          : readDatasetSnapshotFromGraph(ctx.graph, triggerId);
      return new WorkflowDatasetTriggerNodeModel(triggerId, {
        id: triggerId,
        name: snapshot?.name ?? 'Dataset change',
        datasetId: snapshot?.datasetId ?? '',
        criterion: snapshot?.criterion ?? 'new_row',
        trackFields: snapshot?.trackFields ?? null,
        processExistingRows: snapshot?.processExistingRows ?? false,
        runMode: snapshot?.runMode ?? 'automatic',
        enabled: snapshot?.enabled ?? true,
      });
    },
    syncEntityFromGraph: (_ctx, triggerId, model, graph) => {
      const snapshot = readDatasetSnapshotFromGraph(graph, triggerId);
      if (snapshot !== undefined) {
        model.updateSnapshot(snapshot);
      }
    },
  },
};

const eventTriggerRenderableNode: WorkflowTriggerRenderableNode<
  WorkflowTriggerViewData,
  WorkflowEventTriggerNodeModel
> = {
  key: WORKFLOW_RENDER_NODE_KEYS.eventTrigger,
  layer: 'trigger',
  matches: isEventTrigger,
  view: eventTriggerStrategy,
  models: {
    listEntityIdsInGraph: (graph) =>
      graph.triggers
        .filter(
          (snapshot) => snapshot.kind === PersistedWorkflowTriggerKind.Event
        )
        .map((snapshot) => snapshot.trigger.id),
    createFullModel: (ctx, triggerId) => {
      const snapshot =
        ctx.graph === null
          ? undefined
          : readEventSnapshotFromGraph(ctx.graph, triggerId);
      return new WorkflowEventTriggerNodeModel(triggerId, {
        id: triggerId,
        name: snapshot?.name ?? 'Event',
        eventName: snapshot?.eventName ?? '',
        enabled: snapshot?.enabled ?? true,
      });
    },
    syncEntityFromGraph: (_ctx, triggerId, model, graph) => {
      const snapshot = readEventSnapshotFromGraph(graph, triggerId);
      if (snapshot !== undefined) {
        model.updateSnapshot(snapshot);
      }
    },
  },
};

const agentGraphNode: WorkflowGraphNodeRenderableNode<
  WorkflowNodeViewData,
  WorkflowAgentNodeModel
> = {
  key: WORKFLOW_RENDER_NODE_KEYS.agent,
  layer: 'graph-node',
  nodeType: WorkflowNodeType.Agent,
  matches: (node) => node.type === WorkflowNodeType.Agent,
  view: agentNodeStrategy,
  models: {
    listEntityIdsInGraph: readAgentIdsFromGraph,
    createFullModel: (ctx, agentId) =>
      new WorkflowAgentNodeModel({
        agentId,
        client: ctx.client,
        watchablesFactory: ctx.watchablesFactory,
      }),
  },
  resolveModelEntityId: agentIdFromNode,
  renderFlowNode: renderAgentFlowNode,
};

function viewOnlyGraphNode(
  key: string,
  nodeType: string,
  view: WorkflowGraphNodeRenderableNode<WorkflowNodeViewData>['view'],
  flow?: Pick<
    WorkflowGraphNodeRenderableNode<WorkflowNodeViewData>,
    'renderFlowNode' | 'renderFlowAffordances'
  >
): WorkflowGraphNodeRenderableNode<WorkflowNodeViewData> {
  return {
    key,
    layer: 'graph-node',
    nodeType,
    matches: (node) => node.type === nodeType,
    view,
    ...flow,
  };
}

function viewOnlyTrigger(
  key: string,
  view: WorkflowTriggerRenderableNode<WorkflowTriggerViewData>['view'],
  matches: WorkflowTriggerRenderableNode<WorkflowTriggerViewData>['matches']
): WorkflowTriggerRenderableNode<WorkflowTriggerViewData> {
  return {
    key,
    layer: 'trigger',
    matches,
    view,
  };
}

/**
 * Default Cotera workflow canvas catalog: trigger + graph node views with
 * optional per-entity view models (webhook preview/full, cron, agent).
 */
export const DEFAULT_WORKFLOW_RENDER_CATALOG = {
  triggers: [
    webhookTriggerRenderableNode,
    cronTriggerRenderableNode,
    eventTriggerRenderableNode,
    datasetTriggerRenderableNode,
  ],
  graphNodes: [
    agentGraphNode,
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.forEach,
      WorkflowNodeType.ForEach,
      forEachNodeStrategy,
      { renderFlowNode: renderForEachFlowNode }
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.adHocAgent,
      WorkflowNodeType.AdHocAgent,
      adHocAgentNodeStrategy
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.completeToolCall,
      WorkflowNodeType.CompleteToolCall,
      completeToolCallNodeStrategy
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.toolCall,
      WorkflowNodeType.ToolCall,
      toolCallNodeStrategy,
      { renderFlowAffordances: renderToolCallFlowAffordances }
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.filter,
      WorkflowNodeType.Filter,
      filterNodeStrategy
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.debounce,
      WorkflowNodeType.Debounce,
      debounceNodeStrategy
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.emitEvent,
      WorkflowNodeType.EmitEvent,
      emitEventNodeStrategy
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.mapper,
      WorkflowNodeType.Mapper,
      mapperNodeStrategy
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.bucket,
      WorkflowNodeType.Bucket,
      bucketNodeStrategy
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.groupBy,
      WorkflowNodeType.GroupBy,
      groupByNodeStrategy
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.runWorkflow,
      WorkflowNodeType.RunWorkflow,
      runWorkflowNodeStrategy
    ),
    viewOnlyGraphNode(
      WORKFLOW_RENDER_NODE_KEYS.runInput,
      WORKFLOW_RUN_INPUT_NODE_TYPE,
      runInputNodeStrategy
    ),
  ],
  unknownTrigger: viewOnlyTrigger(
    WORKFLOW_RENDER_NODE_KEYS.unknownTrigger,
    unknownTriggerStrategy,
    () => true
  ),
  unknownGraphNode: {
    key: WORKFLOW_RENDER_NODE_KEYS.unknownGraphNode,
    layer: 'graph-node',
    nodeType: 'unknown',
    matches: () => true,
    view: unknownNodeStrategy,
  },
} as WorkflowRenderCatalog;

export function listModelDescriptors(
  catalog: WorkflowRenderCatalog
): Array<
  | WorkflowTriggerRenderableNode<
      WorkflowTriggerViewData,
      WorkflowDisposable,
      WorkflowDisposable
    >
  | WorkflowGraphNodeRenderableNode<
      WorkflowNodeViewData,
      WorkflowDisposable,
      WorkflowDisposable
    >
> {
  return [
    ...catalog.triggers.filter((entry) => entry.models !== undefined),
    ...catalog.graphNodes.filter((entry) => entry.models !== undefined),
  ];
}

export function resolveTriggerRenderableNode(
  catalog: WorkflowRenderCatalog,
  trigger: WorkflowTriggerViewData
): WorkflowTriggerRenderableNode<WorkflowTriggerViewData> {
  for (const entry of catalog.triggers) {
    if (entry.matches(trigger)) {
      return entry;
    }
  }
  return catalog.unknownTrigger;
}

export function resolveGraphNodeRenderableNode(
  catalog: WorkflowRenderCatalog,
  nodeType: string
): WorkflowGraphNodeRenderableNode<WorkflowNodeViewData> {
  for (const entry of catalog.graphNodes) {
    if (entry.nodeType === nodeType) {
      return entry;
    }
  }
  return catalog.unknownGraphNode;
}

export function resolveGraphNodeRenderableNodeByViewData(
  catalog: WorkflowRenderCatalog,
  node: WorkflowNodeViewData
): WorkflowGraphNodeRenderableNode<WorkflowNodeViewData> {
  for (const entry of catalog.graphNodes) {
    if (entry.matches(node)) {
      return entry;
    }
  }
  return catalog.unknownGraphNode;
}

export type { WorkflowNodeModelFactoryContext };
